import { useMemo, useRef, useState } from 'react';
import type { Product, StockMovementKind, Supplier } from '@salonomy/types';
import { Button, Field, Modal, Select, TextInput } from '@salonomy/ui';
import { useCreateStockMovement } from '../../data/stock.js';
import { useToast } from '../../lib/toast.js';
import { penceToPounds, poundsToPence } from '../services/poundsPence.js';
import './stock.css';

/**
 * The movement kinds this manual form posts. We DELIBERATELY offer only receive | waste |
 * count_correction. Two kinds are excluded on purpose:
 *   • 'sell'   — server-forbidden here (a sale posts a movement through the till).
 *   • 'return' — RESERVED by the server contract for the till's REFUND restock path: the engine
 *     treats 'return' as +abs(qty) (applyMovementDelta in api/.../stock/engine.ts), i.e. it ADDS to
 *     on-hand, and service.ts uses it for restockForRefund. Exposing 'return' as a manual "send back
 *     to supplier" lied — it would have RAISED on-hand. A genuine supplier send-back is recorded as a
 *     `count_correction` with a negative delta (or `waste`), both of which reduce on-hand honestly.
 *
 * The server is the source of truth for the SIGN: it derives qtyDelta by kind from the magnitude
 * (receive → +abs, waste → -abs) and preserves the typed sign ONLY for count_correction. So this form
 * sends a POSITIVE magnitude for receive/waste and the SIGNED value only for count_correction (gated by
 * `isCorrection`, derived from the selected kind).
 */
const MOVEMENT_KINDS: Array<{
  value: Extract<StockMovementKind, 'receive' | 'waste' | 'count_correction'>;
  label: string;
  blurb: string;
}> = [
  { value: 'receive', label: 'Receive', blurb: 'Stock in from a supplier (adds to on-hand).' },
  {
    value: 'waste',
    label: 'Waste / write-off',
    blurb: 'Damaged, expired, or sent back to a supplier (removes from on-hand).',
  },
  {
    value: 'count_correction',
    label: 'Count correction',
    blurb: 'A stock-take adjustment — a signed delta (use a minus for a shortfall).',
  },
];

/**
 * Record a stock movement. Append-only: this never edits an existing movement — a correction is a NEW
 * count_correction. The kind selector offers receive | waste | count_correction. The user enters a
 * POSITIVE count for receive/waste and the server derives the sign by kind; for a count_correction the
 * user types the signed delta directly and it is sent verbatim. The `idempotencyKey` is minted ONCE per
 * open (useRef) so a double-tap is a safe replay, and submit is gated on `isPending`.
 */
export function MovementModal({
  products,
  suppliers,
  defaultProductId,
  onClose,
}: {
  products: Product[];
  suppliers: Supplier[];
  defaultProductId?: string;
  onClose: () => void;
}) {
  const create = useCreateStockMovement();
  const toast = useToast();
  // Mint the idempotency key once for this modal instance — a double-tap replays the same intent.
  const idemKey = useRef(crypto.randomUUID()).current;

  const [productId, setProductId] = useState(defaultProductId ?? products[0]?.id ?? '');
  const [kind, setKind] = useState<'receive' | 'waste' | 'count_correction'>('receive');
  const [qty, setQty] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [reason, setReason] = useState('');

  const kindDef = useMemo(() => MOVEMENT_KINDS.find((k) => k.value === kind)!, [kind]);
  const isReceive = kind === 'receive';
  const isCorrection = kind === 'count_correction';
  const activeSuppliers = suppliers.filter((s) => s.active);

  async function submit() {
    if (!productId) {
      toast.error('Choose a product.');
      return;
    }
    const entered = Number(qty);
    if (!Number.isFinite(entered) || entered === 0) {
      toast.error(
        isCorrection
          ? 'Enter a non-zero correction (use a minus for a shortfall).'
          : 'Enter a quantity greater than zero.',
      );
      return;
    }
    if (!isCorrection && entered < 0) {
      toast.error('Enter the quantity as a positive number — the kind sets the direction.');
      return;
    }
    // qtyDelta: for a correction the typed value is already SIGNED and is sent verbatim; for
    // receive/waste we send a POSITIVE magnitude and the server derives the sign from the kind
    // (receive → +abs, waste → -abs). The server is the source of truth for the sign.
    const qtyDelta = isCorrection ? Math.trunc(entered) : Math.abs(Math.trunc(entered));
    const unitCostPence = isReceive ? poundsToPence(unitCost) : null;

    try {
      await create.mutateAsync({
        productId,
        kind,
        qtyDelta,
        unitCostPence: unitCostPence ?? null,
        supplierId: isReceive && supplierId ? supplierId : null,
        reason: reason.trim() || null,
        idempotencyKey: idemKey,
      });
      toast.success('Movement recorded');
      onClose();
    } catch {
      toast.error('Could not record the movement.');
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Record stock movement"
      width={540}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {/* Gate on isPending so a double-tap is a safe idempotent replay, never a second movement. */}
          <Button onClick={submit} disabled={create.isPending}>
            Record movement
          </Button>
        </>
      }
    >
      <Field label="Product">
        <Select value={productId} onChange={(e) => setProductId(e.target.value)}>
          <option value="">Choose a product…</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {!p.active ? ' (archived)' : ''}
            </option>
          ))}
        </Select>
      </Field>

      <Field label="Kind" hint={kindDef.blurb}>
        <Select
          value={kind}
          onChange={(e) => setKind(e.target.value as 'receive' | 'waste' | 'count_correction')}
        >
          {MOVEMENT_KINDS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </Select>
      </Field>

      <div className="stock-modal-grid">
        <Field
          label={isCorrection ? 'Correction (signed)' : 'Quantity'}
          hint={
            isCorrection
              ? 'e.g. -3 for a shortfall, +2 for a surplus.'
              : 'A positive count; the kind sets the direction.'
          }
        >
          <TextInput
            autoFocus
            inputMode={isCorrection ? 'text' : 'numeric'}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            placeholder={isCorrection ? '-3' : '6'}
          />
        </Field>
        {isReceive ? (
          <Field label="Unit cost (£)" hint="Optional — what you paid per unit.">
            <TextInput
              inputMode="decimal"
              value={unitCost}
              onChange={(e) => setUnitCost(e.target.value)}
              placeholder={penceToPounds(0)}
            />
          </Field>
        ) : (
          <div />
        )}
      </div>

      {isReceive ? (
        <Field label="Supplier" hint={activeSuppliers.length ? undefined : 'No suppliers yet.'}>
          <Select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
            <option value="">No supplier</option>
            {activeSuppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}

      <Field label="Reason / note" hint="Optional — recorded on the ledger for the audit trail.">
        <TextInput
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={isCorrection ? 'Stock-take 12 Jun' : 'Optional note'}
        />
      </Field>

      <div className="stock-note muted">
        Movements are append-only — they can&rsquo;t be edited or deleted. To fix a mistake, record
        a new count correction with the opposite delta.
      </div>
    </Modal>
  );
}
